import { useCallback, useEffect, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { TextInput, TextareaInput } from "@/components/ui/Field";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/Autocomplete";
import type { HeroArticleSuggestion } from "@/lib/material";
import { COMMON_MATERIAL_UNITS, normalizeMaterialUnit } from "@/lib/materialUnits";
import { materialArticleSource } from "../lib/sources";
import type { StandardMaterialIn, StandardMaterialOut } from "../lib/settingsApi";

const DEFAULT_EINHEIT = "Stk";

/**
 * Einheit aus dem Artikelstamm: auf die kanonische Schreibweise bringen,
 * unbekannte Einheiten unverändert übernehmen (werden als Extra-Option angezeigt).
 */
function unitFromArticle(art: HeroArticleSuggestion | null | undefined): string {
  const raw = (art?.einheit || art?.hero_unit || "").trim();
  if (!raw) return DEFAULT_EINHEIT;
  return normalizeMaterialUnit(raw) || raw;
}

export interface StandardMaterialApi {
  list: () => Promise<StandardMaterialOut[]>;
  create: (body: StandardMaterialIn) => Promise<StandardMaterialOut>;
  update: (id: number, body: StandardMaterialIn) => Promise<StandardMaterialOut>;
  remove: (id: number) => Promise<void>;
}

export interface StandardMaterialDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  api: StandardMaterialApi;
}

export function StandardMaterialDialog({ open, onClose, title, api }: StandardMaterialDialogProps) {
  const [items, setItems] = useState<StandardMaterialOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [material, setMaterial] = useState<AutocompleteOption<HeroArticleSuggestion> | null>(null);
  const [menge, setMenge] = useState("1");
  const [einheit, setEinheit] = useState(DEFAULT_EINHEIT);
  const [bemerkung, setBemerkung] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await api.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Materialien konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  function resetPicker() {
    setMaterial(null);
    setMenge("1");
    setEinheit(DEFAULT_EINHEIT);
    setBemerkung("");
    setPickerOpen(false);
  }

  /** Artikel gewählt → Einheit direkt aus dem Artikelstamm übernehmen. */
  function handleMaterialChange(next: AutocompleteOption<HeroArticleSuggestion> | null) {
    setMaterial(next);
    if (next?.value && (next.value.artikel_id != null || next.value.hero_nr)) {
      setEinheit(unitFromArticle(next.value));
    }
  }

  async function handleAdd() {
    const text = material?.label?.trim() || "";
    if (!text) {
      setError("Bitte ein Material auswählen oder eingeben.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const art = material?.value;
      await api.create({
        text,
        menge: parseFloat(menge) || 1,
        einheit: einheit.trim() || unitFromArticle(art),
        bemerkung: bemerkung.trim() || null,
        hero_nr: art?.hero_nr || null,
        artikel_id: art?.artikel_id ?? null,
      });
      resetPicker();
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Hinzufügen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Standardmaterial wirklich entfernen?")) return;
    try {
      await api.remove(id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  const pickerDirty =
    pickerOpen &&
    Boolean(
      material?.label?.trim() ||
        menge.trim() !== "1" ||
        einheit !== DEFAULT_EINHEIT ||
        bemerkung.trim(),
    );

  return (
    <StandardMaterialDialogView
      open={open}
      onClose={onClose}
      dirty={pickerDirty}
      title={title}
      error={error}
      loading={loading}
      items={items}
      pickerOpen={pickerOpen}
      material={material}
      menge={menge}
      einheit={einheit}
      bemerkung={bemerkung}
      saving={saving}
      onDelete={handleDelete}
      onOpenPicker={() => setPickerOpen(true)}
      onMaterialChange={handleMaterialChange}
      onMengeChange={setMenge}
      onEinheitChange={setEinheit}
      onBemerkungChange={setBemerkung}
      onAdd={() => void handleAdd()}
      onResetPicker={resetPicker}
    />
  );
}

function StandardMaterialDialogView({
  open,
  onClose,
  dirty,
  title,
  error,
  loading,
  items,
  pickerOpen,
  material,
  menge,
  einheit,
  bemerkung,
  saving,
  onDelete,
  onOpenPicker,
  onMaterialChange,
  onMengeChange,
  onEinheitChange,
  onBemerkungChange,
  onAdd,
  onResetPicker,
}: {
  open: boolean;
  onClose: () => void;
  dirty: boolean;
  title: string;
  error: string | null;
  loading: boolean;
  items: StandardMaterialOut[];
  pickerOpen: boolean;
  material: AutocompleteOption<HeroArticleSuggestion> | null;
  menge: string;
  einheit: string;
  bemerkung: string;
  saving: boolean;
  onDelete: (id: number) => void;
  onOpenPicker: () => void;
  onMaterialChange: (v: AutocompleteOption<HeroArticleSuggestion> | null) => void;
  onMengeChange: (v: string) => void;
  onEinheitChange: (v: string) => void;
  onBemerkungChange: (v: string) => void;
  onAdd: () => void;
  onResetPicker: () => void;
}) {
  const requestClose = useDialogRequestClose(onClose);

  // Unbekannte Einheit aus dem Artikelstamm (z. B. Hero-Sonderfall) als Extra-Option anbieten.
  const einheitOptions: readonly string[] =
    einheit && !(COMMON_MATERIAL_UNITS as readonly string[]).includes(einheit)
      ? [einheit, ...COMMON_MATERIAL_UNITS]
      : COMMON_MATERIAL_UNITS;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={title}
      size="lg"
      footer={
        <Button type="button" variant="secondary" onClick={requestClose}>
          Schließen
        </Button>
      }
    >
      {error && (
        <Alert variant="error" title="Fehler" className="mb-3">
          {error}
        </Alert>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      ) : (
        <ul className="divide-y divide-separator rounded-ios border border-separator">
          {items.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-label-secondary">
              Noch keine Standardmaterialien hinterlegt.
            </li>
          )}
          {items.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium text-label">{item.text}</p>
                <p className="text-sm text-label-secondary">
                  {item.menge} {item.einheit || ""}
                  {item.hero_nr ? ` · Hero ${item.hero_nr}` : ""}
                </p>
                {item.bemerkung && (
                  <p className="mt-1 text-xs text-label-tertiary">{item.bemerkung}</p>
                )}
              </div>
              <Button type="button" variant="danger" size="sm" onClick={() => onDelete(item.id)}>
                Entfernen
              </Button>
            </li>
          ))}
        </ul>
      )}

      {!pickerOpen ? (
        <Button type="button" variant="secondary" className="mt-3" onClick={onOpenPicker}>
          Artikel auswählen
        </Button>
      ) : (
        <div className="mt-4 space-y-3 rounded-ios border border-separator bg-card-elevated p-4">
          <LockedAutocomplete<HeroArticleSuggestion>
            label="Material"
            placeholder="Material suchen …"
            value={material}
            minChars={2}
            loadOptions={materialArticleSource}
            onChange={onMaterialChange}
            allowFreeText
            createFreeTextOption={(q) => ({
              key: `free:${q}`,
              label: q,
              value: { label: q, value: q, artikel_id: null, hero_nr: null, hero_unit: null, einheit: null, ean: null },
            })}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Menge" type="number" min={0} step="0.01" value={menge} onChange={(e) => onMengeChange(e.target.value)} />
            <NativeSelectField
              label="Einheit"
              value={einheit}
              onChange={(e) => onEinheitChange(e.target.value)}
            >
              {einheitOptions.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </NativeSelectField>
          </div>
          <TextareaInput label="Bemerkung" value={bemerkung} onChange={(e) => onBemerkungChange(e.target.value)} rows={2} />
          <div className="flex gap-2">
            <Button type="button" onClick={onAdd} disabled={saving}>
              Hinzufügen
            </Button>
            <Button type="button" variant="secondary" onClick={onResetPicker}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
